import React, { useEffect, useMemo } from 'react';
import { useIntl } from 'react-intl';
import { FlagProvider, useFlags, useFlagsStatus } from '@unleash/proxy-client-react';
import { Loader } from '@sdir/sds';
import { ErrorMessage } from '@sdir/utilities/lib/components';
import { FeatureToggleContext } from '../../../services/contexts/FeatureToggleContext';

const config = {
  url: process.env.REACT_APP_UNLEASH_URL as string,
  clientKey: process.env.REACT_APP_UNLEASH_AUTHORIZATION as string,
  refreshInterval: 300,
  appName: 'mss',
};

const FeatureToggleProvider: React.FC<{ children: any }> = ({ children }) => {
  const toggles = useFlags();
  const intl = useIntl();
  const { flagsReady, flagsError } = useFlagsStatus();

  const value = useMemo(() => {
    if (toggles) {
      return { result: toggles };
    }
    if (!flagsReady) {
      return { loading: true };
    }
    if (flagsError) {
      return { error: flagsError };
    }
    return { result: [] };
  }, [flagsError, flagsReady, toggles]);

  useEffect(() => {
    const storage = window?.sessionStorage;
    if (toggles && storage) {
      storage.setItem('toggles', JSON.stringify(toggles));
    }
  }, [toggles]);

  if (value?.error) {
    return <ErrorMessage error={intl.formatMessage({ id: 'general.error.failedtoload' })} />;
  }

  if (value?.loading) {
    return <Loader />;
  }

  if (!value?.result || value.result.length === 0) return <></>;

  return <FeatureToggleContext.Provider value={value}>{children}</FeatureToggleContext.Provider>;
};

export const UnleashProvider: React.FC<{ children: any }> = ({ children }) => {
  return (
    <FlagProvider config={config}>
      <FeatureToggleProvider>{children}</FeatureToggleProvider>
    </FlagProvider>
  );
};
